import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useOCPP } from '../contexts/OCPPContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiClient } from '../api/client';
import { formatConnectorSoc } from '../utils/format';
import {
  FALLBACK_HOLD_SECS,
  buildChargingProfilePayload,
  loadSavedProfileLimits,
  profileSubmitMessage,
  saveProfileLimits,
} from '../utils/chargingProfile';
import {
  OPERATOR_CONFIG_KEYS,
  configKeyName,
  configKeyReadonly,
  configKeyValue,
  isInterestingConfigKey,
  isTruthyConfig,
} from '../utils/ocppOperatorConfig';

const ChargerDetails = () => {
  const { chargerId } = useParams();
  const navigate = useNavigate();
  const { chargers, emergencyStop, setChargingProfile, clearChargingProfile, getCompositeSchedule, removeCharger, fetchChargers } = useOCPP();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [emergencyLoading, setEmergencyLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [clearLoadingMap, setClearLoadingMap] = useState({});
  const [scheduleCapability, setScheduleCapability] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [notes, setNotes] = useState('');
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [aliasName, setAliasName] = useState('');
  const [startIdTag, setStartIdTag] = useState('');
  const [startConnector, setStartConnector] = useState('');
  const [startLoading, setStartLoading] = useState(false);
  const [stopLoading, setStopLoading] = useState(false);
  const [ocppConfig, setOcppConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configFilter, setConfigFilter] = useState('');
  const [configError, setConfigError] = useState('');
  const [configInterestingOnly, setConfigInterestingOnly] = useState(false);
  const [configCopied, setConfigCopied] = useState(false);
  const [socLoading, setSocLoading] = useState(false);
  const [configChangingKey, setConfigChangingKey] = useState('');
  const [defaultIdTagDraft, setDefaultIdTagDraft] = useState('');

  const api = apiClient;
  const [profileForm, setProfileForm] = useState({
    mode: 'fallback',
    duration: 60,
    stack_level: 5,
    purpose: 'TxDefaultProfile',
    connector_id: 0,
    charging_rate_unit: 'W',
    number_phases: 3,
    profile_kind: 'Absolute',
    recurrency_kind: '',
    overlay_limit: 44000,
    fallback_limit: 20000,
    periods: [{ startPeriod: 0, limit: 53000, numberPhases: 3 }]
  });
  const [schedulePollingInterval, setSchedulePollingInterval] = useState(5); // seconds
  const [isSchedulePollingActive, setIsSchedulePollingActive] = useState(false);
  const [nextUpdateCountdown, setNextUpdateCountdown] = useState(0);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);

  // Decode the charger ID from URL params
  const decodedChargerId = decodeURIComponent(chargerId);
  const charger = chargers[decodedChargerId];

  // Load charger metadata (display_name, notes)
  useEffect(() => {
    if (charger) {
      setDisplayName(charger.display_name || '');
      setNotes(charger.notes || '');
    }
  }, [charger?.display_name, charger?.notes]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const saved = loadSavedProfileLimits(decodedChargerId);
    if (!saved) return;
    setProfileForm((prev) => ({
      ...prev,
      ...saved,
    }));
  }, [decodedChargerId]);

  const loadVehicles = async () => {
    try {
      const resp = await api.get('/vehicles');
      setVehicles(resp.data.vehicles || []);
    } catch (err) {
      // admin-only; ignore for non-admin
    }
  };

  useEffect(() => {
    loadVehicles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const ident = charger?.last_vehicle?.identifier;
    if (ident && !startIdTag) setStartIdTag(ident);
    const conn = charger?.last_vehicle?.connector_id;
    if (conn && startConnector === '') setStartConnector(String(conn));
  }, [charger?.last_vehicle?.identifier, charger?.last_vehicle?.connector_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveMetadata = async () => {
    setMetadataLoading(true);
    try {
      await api.patch(`/chargers/${encodeURIComponent(decodedChargerId)}`, {
        display_name: (displayName || '').trim() || null,
        notes: (notes || '').trim() || null
      });
      addToast('Charger label saved', 'success');
      if (fetchChargers) await fetchChargers();
    } catch (err) {
      addToast('Failed to save charger label: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setMetadataLoading(false);
    }
  };

  const handleDeleteCharger = async () => {
    const online = charger?.connected || charger?.connection_status === 'Connected';
    if (online) {
      addToast('Disconnect the charger first, then you can remove it.', 'error');
      return;
    }
    if (!window.confirm(
      `Remove charger ${decodedChargerId} from this server?\n\nThis deletes its database record, meter history, and OCPP traces. If it connects again it will reappear.`
    )) {
      return;
    }
    setDeleteLoading(true);
    try {
      await api.delete(`/chargers/${encodeURIComponent(decodedChargerId)}?confirm=true`);
      if (removeCharger) removeCharger(decodedChargerId);
      addToast(`Removed ${decodedChargerId}`, 'success');
      navigate('/');
    } catch (err) {
      addToast('Failed to remove charger: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  const vehicleName = (identifier) => {
    if (!identifier) return '';
    const row = vehicles.find((v) => v.identifier === identifier);
    return row?.name || '';
  };

  const handleSaveAlias = async (identifier, kind) => {
    if (!identifier) return;
    try {
      await api.put(`/vehicles/${encodeURIComponent(identifier)}`, {
        name: aliasName || identifier,
        kind: kind || 'idTag',
      });
      addToast('Fleet name saved', 'success');
      setAliasName('');
      await loadVehicles();
    } catch (err) {
      addToast('Failed to save name: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleRemoteStart = async (override = {}) => {
    const idTag = (override.idTag || startIdTag || charger?.last_vehicle?.identifier || '').trim();
    const connector = override.connector != null ? String(override.connector) : startConnector;
    if (!idTag) {
      addToast('Enter an idTag (RFID / MAC / mapped id). OCPP 1.6 has no VIN field.', 'error');
      return;
    }
    if (connector === '') {
      addToast('Pick a connector. Sicon rejects RemoteStart without connectorId.', 'error');
      return;
    }
    if (!window.confirm(`Remote start ${decodedChargerId} connector ${connector} with idTag ${idTag}?`)) return;
    setStartLoading(true);
    try {
      const body = { id_tag: idTag, connector_id: parseInt(connector, 10) };
      const resp = await api.post(
        `/chargers/${encodeURIComponent(decodedChargerId)}/remote-start?confirm=true`,
        body
      );
      addToast(
        resp.data.status === 'accepted'
          ? `Remote start accepted (${idTag})`
          : `Charger rejected remote start (${idTag})`,
        resp.data.status === 'accepted' ? 'success' : 'error'
      );
    } catch (err) {
      addToast('Remote start failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setStartLoading(false);
    }
  };

  const handleLoadOcppConfig = async () => {
    setConfigLoading(true);
    setConfigError('');
    setConfigCopied(false);
    try {
      const resp = await api.get(
        `/chargers/${encodeURIComponent(decodedChargerId)}/configuration`,
        { timeout: 60000 }
      );
      const keys = resp.data.configuration_key || [];
      setOcppConfig({
        configuration_key: keys,
        unknown_key: resp.data.unknown_key || [],
        loaded_at: new Date().toISOString(),
      });
      const defaultTag = keys.find((row) => configKeyName(row) === 'DefaultIdTag');
      if (defaultTag) setDefaultIdTagDraft(String(configKeyValue(defaultTag) || ''));
      addToast(`Read ${keys.length} configuration key${keys.length === 1 ? '' : 's'} from charger`, 'success');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setConfigError(msg);
      addToast('Failed to read configuration: ' + msg, 'error');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleCopyOcppConfig = async () => {
    if (!ocppConfig) return;
    const text = JSON.stringify(ocppConfig, null, 2);
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (!ok) throw new Error('copy command failed');
      }
      setConfigCopied(true);
      addToast('Configuration JSON copied', 'success');
      setTimeout(() => setConfigCopied(false), 2000);
    } catch (err) {
      addToast('Could not copy configuration: ' + err.message, 'error');
    }
  };

  const handleDownloadOcppConfig = () => {
    if (!ocppConfig) return;
    const text = JSON.stringify(ocppConfig, null, 2);
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${decodedChargerId}-ocpp-config.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    addToast('Configuration JSON downloaded', 'success');
  };

  const handleEnableSoc = async () => {
    if (!window.confirm(
      `Ask ${decodedChargerId} to include SoC in MeterValues?\n\n`
      + 'Sets Energy+Power+SoC (sampled/clock-aligned) and SoC on stop. '
      + 'If the charger rejects the combined list it may keep Power-only. Watch the next MeterValues in OCPP Trace.'
    )) return;
    setSocLoading(true);
    try {
      const resp = await api.post(
        `/chargers/${encodeURIComponent(decodedChargerId)}/enable-soc?confirm=true`,
        {},
        { timeout: 60000 }
      );
      const r = resp.data.result || {};
      addToast(
        `SoC config: sampled=${r.MeterValuesSampledData || '?'} aligned=${r.MeterValuesAlignedData || '?'} stop=${r.StopTxnSampledData || '?'} trigger=${r.TriggerMessage || '?'}`,
        'success'
      );
      await handleLoadOcppConfig();
    } catch (err) {
      addToast('SoC config failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setSocLoading(false);
    }
  };

  const handleChangeOcppConfig = async (key, value, extraConfirm) => {
    const current = String(configKeyValue(
      (ocppConfig?.configuration_key || []).find((row) => configKeyName(row) === key)
    ) ?? '');
    if (String(value) === current) {
      addToast(`${key} is already ${current || '(empty)'}`, 'info');
      return;
    }
    let msg = `Send ChangeConfiguration ${key}=${value || '(empty)'} to ${decodedChargerId}?\n\nThis talks to the live charger.`;
    if (extraConfirm) {
      msg += '\n\nThis can start charging when a car is plugged in, with no RFID and no RemoteStart.';
    }
    if (!window.confirm(msg)) return;
    setConfigChangingKey(key);
    try {
      const resp = await api.post(
        `/chargers/${encodeURIComponent(decodedChargerId)}/configuration?confirm=true`,
        { key, value: String(value) },
        { timeout: 60000 }
      );
      const ocppStatus = resp.data.result?.status || resp.data.status;
      if (resp.data.status === 'rejected') {
        addToast(`${key} rejected by charger (${ocppStatus})`, 'error');
        return;
      }
      addToast(
        resp.data.result?.reboot_required
          ? `${key} accepted, charger wants a reboot`
          : `${key} set to ${value || '(empty)'}`,
        'success'
      );
      await handleLoadOcppConfig();
    } catch (err) {
      addToast('Failed to change configuration: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setConfigChangingKey('');
    }
  };

  const handleRemoteStop = async () => {
    if (!window.confirm(`Remote stop active transaction(s) on ${decodedChargerId}?`)) return;
    setStopLoading(true);
    try {
      const body = {};
      if (startConnector !== '') body.connector_id = parseInt(startConnector, 10);
      const resp = await api.post(
        `/chargers/${encodeURIComponent(decodedChargerId)}/remote-stop?confirm=true`,
        body
      );
      addToast('Remote stop sent', 'success');
      return resp.data;
    } catch (err) {
      addToast('Remote stop failed: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setStopLoading(false);
    }
  };

  // No automatic loading - schedule only loads when manually requested or polling is active

  // Handle schedule polling
  useEffect(() => {
    let timeoutId;
    let countdownId;
    let isActive = true;
    
    const performPolling = async () => {
      if (!isActive || !isSchedulePollingActive || !charger || !getCompositeSchedule) {
        return;
      }
      
      setIsLoadingSchedule(true);
      try {
        const result = await getCompositeSchedule(decodedChargerId);
        if (isActive) {
          setScheduleCapability(result);
        }
      } catch (error) {
        // polling error - continue silently
      } finally {
        if (isActive) {
          setIsLoadingSchedule(false);
        }
        
        // Schedule next poll only if still active and polling is enabled
        if (isActive && isSchedulePollingActive) {
          setNextUpdateCountdown(schedulePollingInterval);
          timeoutId = setTimeout(() => {
            if (isActive && isSchedulePollingActive) {
              performPolling();
            }
          }, schedulePollingInterval * 1000);
        }
      }
    };
    
    if (isSchedulePollingActive && charger && getCompositeSchedule) {
      // Start first poll immediately
      setNextUpdateCountdown(schedulePollingInterval);
      performPolling();
      
      // Countdown timer (every second)
      countdownId = setInterval(() => {
        if (isActive) {
          setNextUpdateCountdown(prev => {
            if (prev <= 1) {
              return schedulePollingInterval;
            }
            return prev - 1;
          });
        }
      }, 1000);
    } else {
      // Reset countdown when polling stops
      setNextUpdateCountdown(0);
    }
    
    return () => {
      isActive = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (countdownId) {
        clearInterval(countdownId);
      }
    };
  }, [isSchedulePollingActive, schedulePollingInterval, charger, decodedChargerId, getCompositeSchedule]);

  if (!charger) {
    return (
      <div>
        <Link to="/" className="btn btn-primary mb-4">← Back to Dashboard</Link>
        <div className="card text-center">
          <h2>Charger Not Found</h2>
          <p>Charger "{decodedChargerId}" not found or not connected.</p>
        </div>
      </div>
    );
  }

  const handleEmergencyStop = async (method) => {
    if (!window.confirm(`Are you sure you want to execute ${method} emergency stop?`)) {
      return;
    }

    setEmergencyLoading(true);
    try {
      await emergencyStop(decodedChargerId, method);
      addToast('Emergency stop executed successfully', 'success');
    } catch (error) {
      addToast(`Emergency stop failed: ${error.message}`, 'error');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleSetProfile = async (e) => {
    e.preventDefault();

    setProfileLoading(true);
    try {
      const profile = buildChargingProfilePayload(profileForm);
      await setChargingProfile(decodedChargerId, profile);
      saveProfileLimits(decodedChargerId, profileForm);
      addToast(profileSubmitMessage(profileForm), 'success');
    } catch (error) {
      addToast(`Failed to set charging profile: ${error.message}`, 'error');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleClearProfile = async (clearType) => {
    const confirmMessages = {
      'all': 'Are you sure you want to clear ALL charging profiles? This will remove all power limits.',
      'stack_level': 'Clear all profiles at the current stack level?',
      'defaults': 'Clear all default profiles (TxDefaultProfile)? This removes permanent fallback profiles.',
      'temporary': 'Clear all temporary profiles (ChargePointMaxProfile)? Default profiles will remain active.'
    };
    
    if (!window.confirm(confirmMessages[clearType])) {
      return;
    }

    setClearLoadingMap(prev => ({ ...prev, [clearType]: true }));
    try {
      let options = { connectorId: parseInt(profileForm.connector_id) };

      switch (clearType) {
        case 'all':
          break;
        case 'stack_level':
          options.stackLevel = profileForm.mode === 'cap' ? profileForm.stack_level : 0;
          break;
        case 'defaults':
          options.purpose = 'TxDefaultProfile';
          break;
        case 'temporary':
          options.purpose = 'ChargePointMaxProfile';
          break;
        default:
          break;
      }

      await clearChargingProfile(decodedChargerId, options);

      const successMessages = {
        'all': 'All charging profiles cleared successfully',
        'stack_level': `All profiles at stack level ${options.stackLevel} cleared successfully`,
        'defaults': 'All default profiles cleared successfully',
        'temporary': 'All temporary profiles cleared successfully'
      };

      addToast(successMessages[clearType], 'success');

      if (getCompositeSchedule) {
        getCompositeSchedule(decodedChargerId).then(result => {
          setScheduleCapability(result);
        });
      }

    } catch (error) {
      addToast(`Failed to clear charging profiles: ${error.message}`, 'error');
    } finally {
      setClearLoadingMap(prev => ({ ...prev, [clearType]: false }));
    }
  };

  const formatPower = (watts) => {
    if (watts == null || watts === '' || Number(watts) === 0) return '0 W';
    if (watts >= 1000) return `${(watts / 1000).toFixed(1)} kW`;
    return `${watts} W`;
  };

  const formatScheduleLimit = (limit, unit) => {
    const u = (unit || 'W').toUpperCase();
    if (u === 'A') return `${limit} A`;
    return formatPower(limit);
  };

  const formatDurationSecs = (seconds) => {
    const s = parseInt(seconds, 10) || 0;
    if (s < 60) return `${s}s`;
    if (s < 3600) {
      const m = Math.floor(s / 60);
      const r = s % 60;
      return r ? `${m}m ${r}s` : `${m}m`;
    }
    if (s >= 86400) {
      const d = Math.floor(s / 86400);
      const h = Math.floor((s % 86400) / 3600);
      return h ? `${d}d ${h}h` : `${d}d`;
    }
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  };

  const profileMode = profileForm.mode || 'fallback';
  const clearStackLevel = profileMode === 'cap' ? profileForm.stack_level : 0;
  const fallbackHoldLabel = formatDurationSecs(FALLBACK_HOLD_SECS);
  const previewPeriods = profileMode === 'fallback'
    ? [{ startPeriod: 0, limit: profileForm.fallback_limit, numberPhases: profileForm.number_phases }]
    : profileMode === 'timed'
      ? [
          { startPeriod: 0, limit: profileForm.overlay_limit, numberPhases: profileForm.number_phases },
          { startPeriod: profileForm.duration, limit: profileForm.fallback_limit, numberPhases: profileForm.number_phases },
        ]
      : profileForm.periods;

  const connectors = charger.connectors || [];
  const liveCurrentPower = connectors.reduce((total, conn) => total + (conn.current_power || 0), 0)
    || charger.current_power || 0;
  const liveMaxPower = connectors.reduce((total, conn) => total + (conn.max_power || 0), 0)
    || charger.max_power || 0;

  const allConfigKeys = (ocppConfig?.configuration_key || []).slice().sort((a, b) => {
    const ka = configKeyName(a);
    const kb = configKeyName(b);
    const ia = isInterestingConfigKey(ka) ? 0 : 1;
    const ib = isInterestingConfigKey(kb) ? 0 : 1;
    if (ia !== ib) return ia - ib;
    return ka.localeCompare(kb);
  });
  const interestingConfigKeys = allConfigKeys.filter((row) => isInterestingConfigKey(configKeyName(row)));
  const configQuery = configFilter.trim().toLowerCase();
  const visibleConfigKeys = allConfigKeys.filter((row) => {
    const key = configKeyName(row);
    if (configInterestingOnly && !isInterestingConfigKey(key)) return false;
    if (!configQuery) return true;
    return key.toLowerCase().includes(configQuery) || String(configKeyValue(row)).toLowerCase().includes(configQuery);
  });

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <Link to="/" className="btn btn-primary">← Back to Dashboard</Link>
      </div>

      <div className="grid grid-2" style={{ marginBottom: '2rem' }}>
        <div className="card">
          <h2>{displayName || charger.id}</h2>
          {displayName && <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.5rem' }}>ID: {charger.id}</div>}

          {/* Charger Label & Notes */}
          <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Display name (optional)"
                style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
              />
              <button
                onClick={handleSaveMetadata}
                disabled={metadataLoading}
                style={{
                  padding: '0.4rem 0.75rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: metadataLoading ? 'not-allowed' : 'pointer',
                  fontSize: '0.85rem',
                  opacity: metadataLoading ? 0.6 : 1
                }}
              >
                {metadataLoading ? 'Saving...' : 'Save charger name'}
              </button>
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optional)"
              rows={2}
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.85rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          <div className="grid" style={{ gap: '0.5rem', gridTemplateColumns: 'auto 1fr' }}>
            <strong>Vendor:</strong>
            <span>{charger.vendor || 'Unknown'}</span>
            
            <strong>Model:</strong>
            <span>{charger.model || 'Unknown'}</span>
            
            <strong>Firmware:</strong>
            <span>{charger.firmware_version || 'Unknown'}</span>
            
            <strong>Status:</strong>
            <span style={{ display: 'flex', alignItems: 'center' }}>
              <span className={`status-indicator ${
                charger.connection_status === 'Connected' ? 'status-connected' : 'status-disconnected'
              }`} />
              {charger.connection_status}
            </span>
            
            <strong>Max Power:</strong>
            <span>{liveMaxPower ? formatPower(liveMaxPower) : 'Unknown (not reported by charger)'}</span>
            
            <strong>Current Power:</strong>
            <span>{formatPower(liveCurrentPower)}</span>

            <strong>OCPP traffic:</strong>
            <span>
              <Link to={`/ocpp-trace?charger=${encodeURIComponent(decodedChargerId)}`}>
                View all messages for this charger
              </Link>
            </span>
            
            <strong>Last Heartbeat:</strong>
            <span>{charger.last_heartbeat ? 
              new Date(charger.last_heartbeat).toLocaleString() : 
              'Never'
            }</span>
          </div>

          {user?.role === 'admin' && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee' }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteCharger}
                disabled={deleteLoading || charger.connected || charger.connection_status === 'Connected'}
                title={
                  charger.connected || charger.connection_status === 'Connected'
                    ? 'Disconnect the charger first'
                    : 'Remove this charger from the server'
                }
              >
                {deleteLoading ? 'Removing…' : 'Remove charger'}
              </button>
              <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.4rem' }}>
                Offline test chargers can be removed. If this ID connects again it will come back.
              </div>
            </div>
          )}
          
          {/* Emergency Response Display */}
          {charger.last_emergency_response && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
              <strong>Last Emergency Action:</strong>
              <div style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                <div><strong>Action:</strong> {charger.last_emergency_response.action}</div>
                <div><strong>Response:</strong> {charger.last_emergency_response.response}</div>
                <div><strong>Time:</strong> {new Date(charger.last_emergency_response.timestamp).toLocaleString()}</div>
              </div>
            </div>
          )}
        </div>

        {user?.role === 'admin' && (
          <div className="card">
            <h3>Vehicle &amp; remote start</h3>
            <p style={{ fontSize: '0.85rem', color: '#555', marginTop: 0 }}>
              OCPP 1.6 has no VIN field. We show RFID idTag, AutoCharge MAC, or vendor DataTransfer
              if the charger sends it. Sitterdorf traces so far had none of these — only plug status
              and power. Manual RemoteStart is the CSMS equivalent of presenting an RFID / pressing start.
              To see whether this station has ISO 15118 / AutoCharge keys, use OCPP configuration below.
            </p>
            <div style={{ marginBottom: '0.75rem', padding: '0.6rem', background: '#f8f9fa', borderRadius: 6, fontSize: '0.9rem' }}>
              <strong>Last seen:</strong>{' '}
              {charger.last_vehicle?.identifier
                ? `${charger.last_vehicle.identifier} (${charger.last_vehicle.kind || 'id'} via ${charger.last_vehicle.source || '?'})`
                : charger.last_vehicle?.raw
                  ? `DataTransfer with no parsed id: ${charger.last_vehicle.raw}`
                  : 'nothing yet — plug a car or badge RFID'}
              {vehicleName(charger.last_vehicle?.identifier) ? ` — ${vehicleName(charger.last_vehicle.identifier)}` : ''}
            </div>
            {charger.last_vehicle?.identifier && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  placeholder="Fleet name for this id"
                  value={aliasName}
                  onChange={(e) => setAliasName(e.target.value)}
                  style={{ flex: 1, padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4 }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleSaveAlias(charger.last_vehicle.identifier, charger.last_vehicle.kind)}
                >
                  Save name
                </button>
              </div>
            )}
            <p style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
              <Link to="/fleet">Open fleet list</Link> to rename IDs and enable autostart.
            </p>
            {vehicles.length > 0 && (
              <div style={{ fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                <strong>Fleet map</strong>
                {vehicles.map((v) => (
                  <div key={v.identifier} style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                    <span>
                      <code>{v.identifier}</code> {v.name ? `— ${v.name}` : '(unnamed)'}
                    </span>
                    <span style={{ color: '#888' }}>{v.kind}</span>
                  </div>
                ))}
              </div>
            )}
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>idTag for RemoteStart</label>
            <input
              type="text"
              maxLength={20}
              value={startIdTag}
              onChange={(e) => setStartIdTag(e.target.value)}
              placeholder="RFID / MAC / mapped id (max 20)"
              style={{ width: '100%', padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, marginBottom: '0.5rem', boxSizing: 'border-box' }}
            />
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Connector</label>
            <select
              value={startConnector}
              onChange={(e) => setStartConnector(e.target.value)}
              style={{ width: '100%', padding: '0.4rem 0.5rem', marginBottom: '0.75rem' }}
            >
              <option value="">Select connector (required)</option>
              {connectors.filter((c) => c.id).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.id} ({c.status || '?'})
                </option>
              ))}
            </select>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRemoteStart}
                disabled={startLoading || charger.connection_status !== 'Connected'}
              >
                {startLoading ? 'Starting…' : 'Remote start'}
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleRemoteStop}
                disabled={stopLoading || charger.connection_status !== 'Connected'}
              >
                {stopLoading ? 'Stopping…' : 'Remote stop'}
              </button>
            </div>
          </div>
        )}

        <div className="card emergency-panel">
          <h3>Emergency Controls</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <button
                onClick={() => handleEmergencyStop('zero_power')}
                disabled={emergencyLoading || charger.connection_status !== 'Connected'}
                className="btn btn-warning"
                style={{ width: '100%', marginBottom: '0.25rem' }}
              >
                Set Zero Power (Safe Mode)
              </button>
              <small style={{ color: '#666', display: 'block', marginBottom: '0.75rem' }}>
                Sets charging limit to 0W. Session continues but no power flows. Charger stays online.
              </small>
            </div>
            
            <div>
              <button
                onClick={() => handleEmergencyStop('stop_transaction')}
                disabled={emergencyLoading || charger.connection_status !== 'Connected'}
                className="btn btn-danger"
                style={{ width: '100%', marginBottom: '0.25rem' }}
              >
                Stop All Transactions
              </button>
              <small style={{ color: '#666', display: 'block', marginBottom: '0.75rem' }}>
                Sends RemoteStopTransaction for all active charging sessions. Stops all connectors.
              </small>
            </div>
            
            <div>
              <button
                onClick={() => handleEmergencyStop('reset_soft')}
                disabled={emergencyLoading || charger.connection_status !== 'Connected'}
                className="btn btn-danger"
                style={{ width: '100%', marginBottom: '0.25rem' }}
              >
                Soft Reset (Restart Software)
              </button>
              <small style={{ color: '#666', display: 'block', marginBottom: '0.75rem' }}>
                Restarts charger software gracefully. Terminates sessions, saves state, reconnects after restart.
              </small>
            </div>
            
            <div>
              <button
                onClick={() => handleEmergencyStop('reset_hard')}
                disabled={emergencyLoading || charger.connection_status !== 'Connected'}
                className="btn btn-danger"
                style={{ width: '100%', marginBottom: '0.25rem', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
              >
                Hard Reset (Full Power Cycle)
              </button>
              <small style={{ color: '#666', display: 'block' }}>
                Complete hardware and software restart. Immediate termination. Use only if soft reset fails.
              </small>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>OCPP configuration</h3>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {ocppConfig && (
              <>
                <button
                  type="button"
                  className="btn"
                  onClick={handleCopyOcppConfig}
                  style={{ background: '#f8f9fa', border: '1px solid #ddd' }}
                >
                  {configCopied ? 'Copied' : 'Copy JSON'}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={handleDownloadOcppConfig}
                  style={{ background: '#f8f9fa', border: '1px solid #ddd' }}
                >
                  Download JSON
                </button>
              </>
            )}
            <button
              type="button"
              className="btn"
              onClick={handleEnableSoc}
              disabled={socLoading || configLoading || (charger.connection_status !== 'Connected' && !charger.connected)}
              style={{ background: '#f8f9fa', border: '1px solid #ddd' }}
            >
              {socLoading ? 'Requesting SoC…' : 'Try SoC metering'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleLoadOcppConfig}
              disabled={configLoading}
              title={
                charger.connection_status !== 'Connected' && !charger.connected
                  ? 'Charger looks offline; the request will fail unless it reconnects'
                  : 'Send GetConfiguration to this charger'
              }
            >
              {configLoading ? 'Reading from charger…' : ocppConfig ? 'Refresh from charger' : 'Read from charger'}
            </button>
          </div>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#555', marginTop: 0 }}>
          Sends OCPP GetConfiguration (all keys). Highlighted rows usually matter for vehicle id and metering.
          Admins can change a small allowlist (EV identity / RemoteStart / open charge). Network, URL, PIN, and power limits stay read-only.
        </p>
        {configError && (
          <p style={{ color: '#721c24', background: '#f8d7da', border: '1px solid #f5c6cb', padding: '0.6rem 0.75rem', borderRadius: 4, fontSize: '0.9rem' }}>
            {configError}
          </p>
        )}
        {!ocppConfig && !configError && (
          <p style={{ color: '#666', fontSize: '0.9rem' }}>
            Not loaded yet. Charger must be online. This talks to the station, so it can take a few seconds.
          </p>
        )}
        {ocppConfig && (
          <>
            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 0 }}>
              {allConfigKeys.length} key{allConfigKeys.length === 1 ? '' : 's'}
              {interestingConfigKeys.length ? ` · ${interestingConfigKeys.length} highlighted` : ''}
              {ocppConfig.loaded_at ? ` · loaded ${new Date(ocppConfig.loaded_at).toLocaleString()}` : ''}
            </p>
            {user?.role === 'admin' && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#eef6ff', border: '1px solid #c5ddf5', borderRadius: 6 }}>
                <strong style={{ display: 'block', marginBottom: '0.35rem' }}>Change on charger</strong>
                <p style={{ fontSize: '0.85rem', color: '#555', margin: '0 0 0.75rem' }}>
                  Sends OCPP ChangeConfiguration for these keys only. For Sitterdorf, turn on Send EVCC MAC first, then plug a car and watch Last seen.
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
                  {OPERATOR_CONFIG_KEYS.map((spec) => {
                    const row = allConfigKeys.find((item) => configKeyName(item) === spec.key);
                    const current = row ? String(configKeyValue(row)) : '';
                    const missing = !row;
                    const readonly = row ? configKeyReadonly(row) : false;
                    const busy = configChangingKey === spec.key;
                    const on = isTruthyConfig(current);
                    return (
                      <div key={spec.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(12rem, 1fr) auto', gap: '0.5rem 1rem', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>{spec.label}</div>
                          <div style={{ fontSize: '0.8rem', color: '#666' }}>
                            <code>{spec.key}</code>
                            {' · '}
                            {missing ? 'not in last dump' : `now ${current || '(empty)'}${readonly ? ', read-only' : ''}`}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#555' }}>{spec.help}</div>
                        </div>
                        {spec.kind === 'idTag' ? (
                          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                            <input
                              type="text"
                              maxLength={20}
                              value={defaultIdTagDraft}
                              onChange={(e) => setDefaultIdTagDraft(e.target.value)}
                              disabled={busy || readonly}
                              placeholder="optional idTag"
                              style={{ width: 140, padding: '0.3rem 0.4rem', border: '1px solid #ddd', borderRadius: 4 }}
                            />
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={busy || readonly}
                              onClick={() => handleChangeOcppConfig(spec.key, defaultIdTagDraft.trim(), spec.extraConfirm)}
                            >
                              {busy ? 'Sending…' : 'Apply'}
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy || readonly}
                            onClick={() => handleChangeOcppConfig(spec.key, on ? 'false' : 'true', spec.extraConfirm && !on)}
                          >
                            {busy ? 'Sending…' : on ? 'Set false' : 'Set true'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {interestingConfigKeys.length > 0 && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6 }}>
                <strong style={{ display: 'block', marginBottom: '0.4rem' }}>Highlighted keys</strong>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(12rem, max-content) 1fr', gap: '0.2rem 1rem', fontSize: '0.85rem' }}>
                  {interestingConfigKeys.map((row, i) => (
                    <React.Fragment key={`int-${configKeyName(row)}-${i}`}>
                      <code>{configKeyName(row)}</code>
                      <span style={{ wordBreak: 'break-word' }}>{String(configKeyValue(row))}</span>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              <input
                type="search"
                placeholder="Filter keys…"
                value={configFilter}
                onChange={(e) => setConfigFilter(e.target.value)}
                style={{ width: '100%', maxWidth: 360, padding: '0.4rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, boxSizing: 'border-box' }}
              />
              <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={configInterestingOnly}
                  onChange={(e) => setConfigInterestingOnly(e.target.checked)}
                />
                Highlighted only
              </label>
            </div>
            {visibleConfigKeys.length === 0 ? (
              <p style={{ color: '#666', fontSize: '0.9rem' }}>No keys match this filter.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '0.35rem 0.5rem' }}>Key</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '0.35rem 0.5rem' }}>Value</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '0.35rem 0.5rem' }}>Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleConfigKeys.map((row, i) => {
                      const key = configKeyName(row);
                      const interesting = isInterestingConfigKey(key);
                      return (
                        <tr key={`${key}-${i}`} style={{ background: interesting ? '#fff8e1' : undefined }}>
                          <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid #f0f0f0', fontFamily: 'monospace', fontWeight: interesting ? 600 : 400 }}>{key}</td>
                          <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid #f0f0f0', wordBreak: 'break-word' }}>{String(configKeyValue(row))}</td>
                          <td style={{ padding: '0.35rem 0.5rem', borderBottom: '1px solid #f0f0f0', color: '#666' }}>{configKeyReadonly(row) ? 'read-only' : 'read/write'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {(ocppConfig.unknown_key || []).length > 0 && (
              <p style={{ fontSize: '0.85rem', color: '#856404', marginTop: '0.75rem' }}>
                Unknown keys: {(ocppConfig.unknown_key || []).join(', ')}
              </p>
            )}
          </>
        )}
      </div>

      <div className="grid grid-2">
        <div className="card">
          <h3>Connectors ({connectors.length})</h3>
          {connectors.length === 0 ? (
            <p>No connector information available</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {connectors.map((connector, index) => (
                <div key={index} className="card" style={{ margin: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h4>Connector {connector.id || index + 1}</h4>
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <span className={`status-indicator ${
                        connector.status === 'Available' ? 'status-available' :
                        connector.status === 'Occupied' || connector.status === 'Charging' ? 'status-charging' :
                        'status-disconnected'
                      }`} />
                      {connector.status}
                    </span>
                  </div>
                  
                  <div className="grid" style={{ gap: '0.25rem', gridTemplateColumns: 'auto 1fr' }}>
                    <strong>Current Power:</strong>
                    <span>{formatPower(connector.current_power || 0)}</span>
                    <strong>SoC:</strong>
                    <span>{formatConnectorSoc(connector) || '—'}</span>

                    <strong>Vehicle id:</strong>
                    <span>
                      {connector.vehicle?.identifier || connector.id_tag
                        ? `${connector.vehicle?.identifier || connector.id_tag}${
                            connector.vehicle?.kind ? ` (${connector.vehicle.kind})` : ''
                          }${
                            vehicleName(connector.vehicle?.identifier || connector.id_tag)
                              ? ` — ${vehicleName(connector.vehicle?.identifier || connector.id_tag)}`
                              : ''
                          }`
                        : (['Preparing', 'Charging', 'SuspendedEV', 'SuspendedEVSE', 'Occupied', 'Finishing'].includes(connector.status)
                          ? 'waiting for RFID / VehicleMac'
                          : 'none')}
                    </span>
                    {connector.status === 'Finishing' && (
                      <>
                        <strong>Retry:</strong>
                        <span>
                          Cable can stay in. Remote start this gun — if Accepted, we can leave Finishing without unplug.
                          {' '}
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={startLoading || charger.connection_status !== 'Connected'}
                            onClick={() => handleRemoteStart({
                              connector: connector.id,
                              idTag: connector.vehicle?.identifier || connector.id_tag || charger.last_vehicle?.identifier || '',
                            })}
                          >
                            Remote start C{connector.id}
                          </button>
                        </span>
                      </>
                    )}
                    
                    {connector.transaction_id && (
                      <>
                        <strong>Transaction ID:</strong>
                        <span>{connector.transaction_id}</span>
                      </>
                    )}
                    
                    {connector.start_time && (
                      <>
                        <strong>Started:</strong>
                        <span>{new Date(connector.start_time).toLocaleString()}</span>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Schedule Polling Controls - Always visible */}
        {charger && (
          <div className="card" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', marginBottom: '1rem' }}>
            <h5 style={{ color: '#495057', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              🔄 Schedule Monitoring
            </h5>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label style={{ fontSize: '0.9rem', color: '#495057' }}>Auto-refresh:</label>
                <button
                  onClick={() => setIsSchedulePollingActive(!isSchedulePollingActive)}
                  style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '4px',
                    border: 'none',
                    backgroundColor: isSchedulePollingActive ? '#28a745' : '#6c757d',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {isSchedulePollingActive ? '✓ ON' : '✗ OFF'}
                </button>
              </div>
              
              {isSchedulePollingActive && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.9rem', color: '#495057' }}>Interval:</label>
                  <select
                    value={schedulePollingInterval}
                    onChange={(e) => setSchedulePollingInterval(Number(e.target.value))}
                    style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      border: '1px solid #ced4da',
                      fontSize: '0.8rem'
                    }}
                  >
                    <option value={5}>5 seconds</option>
                    <option value={10}>10 seconds</option>
                    <option value={15}>15 seconds</option>
                    <option value={30}>30 seconds</option>
                    <option value={60}>1 minute</option>
                  </select>
                </div>
              )}
              
              <button
                onClick={async () => {
                  if (charger && getCompositeSchedule && !isLoadingSchedule) {
                    setIsLoadingSchedule(true);
                    try {
                      const result = await getCompositeSchedule(decodedChargerId);
                      setScheduleCapability(result);
                      // Reset countdown if polling is active
                      if (isSchedulePollingActive) {
                        setNextUpdateCountdown(schedulePollingInterval);
                      }
                    } catch (error) {
                      // schedule load error - silently handled
                    } finally {
                      setIsLoadingSchedule(false);
                    }
                  }
                }}
                style={{
                  padding: '0.25rem 0.75rem',
                  borderRadius: '4px',
                  border: `1px solid ${isLoadingSchedule ? '#6c757d' : '#007bff'}`,
                  backgroundColor: isLoadingSchedule ? '#6c757d' : '#007bff',
                  color: 'white',
                  cursor: isLoadingSchedule ? 'not-allowed' : 'pointer',
                  fontSize: '0.8rem',
                  opacity: isLoadingSchedule ? 0.6 : 1
                }}
                disabled={isLoadingSchedule}
              >
                {isLoadingSchedule ? '⏳ Loading...' : (scheduleCapability ? '🔄 Refresh Now' : '📋 Load Schedule')}
              </button>
              
              {isSchedulePollingActive && (
                <span style={{ fontSize: '0.8rem', color: '#6c757d', fontStyle: 'italic' }}>
                  {isLoadingSchedule ? '⏳ Waiting for response...' : `Next update in ${nextUpdateCountdown}s`}
                </span>
              )}
              
              <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                Status: {scheduleCapability ? 
                  (scheduleCapability.charger_supports_schedule_details ? '✅ Schedule Loaded' : '⚠️ Limited Support') : 
                  '⭕ No Schedule Data'}
              </div>
            </div>
          </div>
        )}

        {/* Current Active Charging Schedule */}
        {scheduleCapability && scheduleCapability.charger_supports_schedule_details && scheduleCapability.charging_schedule && (
          <div className="card" style={{ backgroundColor: '#e8f5e8', border: '1px solid #c3e6cb' }}>
            <h4 style={{ color: '#155724', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
              ⚡ Current Active Charging Schedule (Station-Wide)
            </h4>
            <div style={{ fontSize: '0.9rem' }}>
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.5rem 1rem', alignItems: 'center' }}>
                  <strong>Connector ID:</strong>
                  <span>{scheduleCapability.connector_id} {scheduleCapability.connector_id === 0 ? '(All Connectors)' : ''}</span>
                  
                  <strong>Status:</strong>
                  <span style={{ 
                    color: scheduleCapability.status === 'Accepted' ? '#28a745' : '#dc3545',
                    fontWeight: '600'
                  }}>
                    {scheduleCapability.status}
                  </span>
                  
                  <strong>Schedule Start:</strong>
                  <span>{scheduleCapability.schedule_start ? 
                    new Date(scheduleCapability.schedule_start).toLocaleString() : 'N/A'}</span>
                  
                  <strong>Rate Unit:</strong>
                  <span>{scheduleCapability.charging_schedule.charging_rate_unit || 'W'}</span>
                  
                  <strong>Duration:</strong>
                  <span>{scheduleCapability.charging_schedule.duration ? 
                    `${Math.floor(scheduleCapability.charging_schedule.duration / 3600)}h ${Math.floor((scheduleCapability.charging_schedule.duration % 3600) / 60)}m` : 
                    'Permanent'}</span>
                </div>
              </div>
              
              {/* Power Schedule Periods */}
              {scheduleCapability.charging_schedule.charging_schedule_period && (
                <div>
                  <strong style={{ display: 'block', marginBottom: '0.5rem', color: '#155724' }}>
                    Power Limits:
                  </strong>
                  <div style={{ 
                    background: '#fff', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '4px', 
                    padding: '0.75rem' 
                  }}>
                    {scheduleCapability.charging_schedule.charging_schedule_period.map((period, index) => (
                      <div key={index} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: index < scheduleCapability.charging_schedule.charging_schedule_period.length - 1 ? '0.5rem' : '0',
                        padding: '0.5rem',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '4px'
                      }}>
                        <span style={{ fontWeight: '500' }}>
                          Period {index + 1} (Start: {period.start_period}s)
                        </span>
                        <span style={{ 
                          fontWeight: '700', 
                          fontSize: '1.1rem',
                          color: '#155724'
                        }}>
                          {formatScheduleLimit(period.limit, scheduleCapability.charging_schedule.charging_rate_unit)} Max
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Charging Profile Capability Warning */}
        {scheduleCapability && !scheduleCapability.charger_supports_schedule_details && (
          <div className="card" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7' }}>
            <h4 style={{ color: '#856404', marginBottom: '0.5rem' }}>⚠️ Limited Charging Profile Support</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#856404' }}>
              This charger does not provide detailed charging schedule information. 
              You can still set charging profiles, but you won't be able to see the current active limits.
            </p>
            {scheduleCapability.raw_response && (
              <details style={{ marginTop: '0.5rem' }}>
                <summary style={{ fontSize: '0.8rem', color: '#6c757d', cursor: 'pointer' }}>
                  Show raw charger response
                </summary>
                <pre style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '0.25rem', whiteSpace: 'pre-wrap' }}>
                  {scheduleCapability.raw_response}
                </pre>
              </details>
            )}
          </div>
        )}

        <div className="card">
          <h3>Set Charging Profile</h3>
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
            This charger replaces TxDefault instead of stacking it. A timed solar limit and the
            fallback must live in the <strong>same</strong> profile (stack 0, two periods).
            After the timed window the fallback stays for {fallbackHoldLabel}.
            ChargePointMax is only a station-wide cap (minimum); it cannot raise power.
          </p>

          <div
            role="radiogroup"
            aria-label="Charging profile mode"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: '0.75rem',
              marginBottom: '1.25rem'
            }}
          >
            {[
              {
                id: 'fallback',
                title: 'Fallback',
                desc: `TxDefault stack 0 at the fallback limit for ${fallbackHoldLabel}. Use this when no solar overlay is running.`
              },
              {
                id: 'timed',
                title: 'Timed solar',
                desc: 'One TxDefault: solar limit first, then fallback. Replaces stack 0 so the fallback cannot disappear.'
              },
              {
                id: 'cap',
                title: 'Station cap',
                desc: 'ChargePointMax on connector 0. Tightens the ceiling; cannot raise power above TxDefault.'
              }
            ].map((mode) => {
              const selected = profileMode === mode.id;
              return (
                <label
                  key={mode.id}
                  data-testid={`profile-mode-${mode.id}`}
                  style={{
                    display: 'block',
                    padding: '0.85rem',
                    borderRadius: '6px',
                    border: selected ? '2px solid #1976d2' : '1px solid #ddd',
                    background: selected ? '#f0f7ff' : '#fff',
                    cursor: 'pointer'
                  }}
                >
                  <input
                    type="radio"
                    name="profile-mode"
                    value={mode.id}
                    checked={selected}
                    onChange={() => {
                      const currentDuration = parseInt(profileForm.duration, 10);
                      const timedDurations = [60, 120, 300, 600, 1800, 3600];
                      let nextDuration = profileForm.duration;
                      if (mode.id === 'timed') {
                        nextDuration = timedDurations.includes(currentDuration) ? currentDuration : 60;
                      } else if (mode.id === 'cap') {
                        nextDuration = currentDuration >= 300 ? currentDuration : 86400;
                      }
                      setProfileForm({
                        ...profileForm,
                        mode: mode.id,
                        duration: nextDuration,
                        purpose: mode.id === 'cap' ? 'ChargePointMaxProfile' : 'TxDefaultProfile',
                        connector_id: mode.id === 'cap' ? 0 : profileForm.connector_id,
                      });
                    }}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{ fontWeight: 600 }}>{mode.title}</span>
                  <div style={{ fontSize: '0.8rem', color: '#555', marginTop: '0.35rem' }}>{mode.desc}</div>
                </label>
              );
            })}
          </div>

          <form onSubmit={handleSetProfile}>
            {/* Row: Connector ID + Rate Unit + Phases */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Connector ID:
                </label>
                <select
                  value={profileMode === 'cap' ? 0 : profileForm.connector_id}
                  onChange={(e) => setProfileForm({...profileForm, connector_id: e.target.value})}
                  disabled={profileMode === 'cap'}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="0">0 - All Connectors</option>
                  {connectors.map((c, i) => (
                    <option key={c.id || i + 1} value={c.id || i + 1}>
                      {c.id || i + 1} - Connector {c.id || i + 1}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Rate Unit:
                </label>
                <select
                  value={profileForm.charging_rate_unit}
                  onChange={(e) => setProfileForm({...profileForm, charging_rate_unit: e.target.value})}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="W">W (Watts)</option>
                  <option value="A">A (Amps)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Phases:
                </label>
                <select
                  value={profileForm.number_phases}
                  onChange={(e) => {
                    const phases = parseInt(e.target.value);
                    const updatedPeriods = profileForm.periods.map(p => ({ ...p, numberPhases: phases }));
                    setProfileForm({...profileForm, number_phases: phases, periods: updatedPeriods});
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="1">1 Phase</option>
                  <option value="3">3 Phase</option>
                </select>
              </div>
            </div>

            {profileMode !== 'cap' && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: profileMode === 'timed' ? '1fr 1fr 1fr' : '1fr',
                gap: '1rem',
                marginBottom: '1rem'
              }}>
                {profileMode === 'timed' && (
                  <>
                    <div>
                      <label htmlFor="overlay-limit" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Timed limit ({profileForm.charging_rate_unit}):
                      </label>
                      <input
                        id="overlay-limit"
                        data-testid="overlay-limit"
                        type="number"
                        min="0"
                        max={profileForm.charging_rate_unit === 'W' ? '350000' : '500'}
                        step={profileForm.charging_rate_unit === 'W' ? '1000' : '1'}
                        value={profileForm.overlay_limit}
                        onChange={(e) => setProfileForm({ ...profileForm, overlay_limit: e.target.value })}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                      <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                        {formatScheduleLimit(profileForm.overlay_limit, profileForm.charging_rate_unit)} for the timed window
                      </div>
                    </div>
                    <div>
                      <label htmlFor="overlay-duration" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Timed window:
                      </label>
                      <select
                        id="overlay-duration"
                        data-testid="overlay-duration"
                        value={profileForm.duration}
                        onChange={(e) => setProfileForm({...profileForm, duration: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="60">1 min</option>
                        <option value="120">2 min</option>
                        <option value="300">5 min</option>
                        <option value="600">10 min</option>
                        <option value="1800">30 min</option>
                        <option value="3600">1 hour</option>
                      </select>
                    </div>
                  </>
                )}
                <div>
                  <label htmlFor="fallback-limit" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Fallback limit ({profileForm.charging_rate_unit}):
                  </label>
                  <input
                    id="fallback-limit"
                    data-testid="fallback-limit"
                    type="number"
                    min="0"
                    max={profileForm.charging_rate_unit === 'W' ? '350000' : '500'}
                    step={profileForm.charging_rate_unit === 'W' ? '1000' : '1'}
                    value={profileForm.fallback_limit}
                    onChange={(e) => setProfileForm({ ...profileForm, fallback_limit: e.target.value })}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                    {formatScheduleLimit(profileForm.fallback_limit, profileForm.charging_rate_unit)}
                    {profileMode === 'timed' ? ` after the timed window, for ${fallbackHoldLabel}` : ` for ${fallbackHoldLabel}`}
                  </div>
                </div>
              </div>
            )}

            {profileMode === 'cap' && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: profileForm.profile_kind === 'Recurring' ? '1fr 1fr' : '1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Profile Kind:
                    </label>
                    <select
                      value={profileForm.profile_kind}
                      onChange={(e) => setProfileForm({...profileForm, profile_kind: e.target.value, recurrency_kind: e.target.value === 'Recurring' ? 'Daily' : ''})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="Absolute">Absolute</option>
                      <option value="Relative">Relative</option>
                      <option value="Recurring">Recurring</option>
                    </select>
                  </div>
                  {profileForm.profile_kind === 'Recurring' && (
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                        Recurrence:
                      </label>
                      <select
                        value={profileForm.recurrency_kind}
                        onChange={(e) => setProfileForm({...profileForm, recurrency_kind: e.target.value})}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="Daily">Daily</option>
                        <option value="Weekly">Weekly</option>
                      </select>
                    </div>
                  )}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Valid Duration:
                    </label>
                    <select
                      value={profileForm.duration}
                      onChange={(e) => setProfileForm({...profileForm, duration: e.target.value})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="300">5 Minutes</option>
                      <option value="600">10 Minutes</option>
                      <option value="1800">30 Minutes</option>
                      <option value="3600">1 Hour</option>
                      <option value="7200">2 Hours</option>
                      <option value="14400">4 Hours</option>
                      <option value="28800">8 Hours</option>
                      <option value="43200">12 Hours</option>
                      <option value="86400">24 Hours</option>
                      <option value="604800">1 Week</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '0.9rem' }}>
                      Stack (priority):
                    </label>
                    <select
                      value={profileForm.stack_level}
                      onChange={(e) => setProfileForm({...profileForm, stack_level: e.target.value})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="10">10 — emergency / highest</option>
                      <option value="5">5 — recommended cap</option>
                      <option value="3">3</option>
                      <option value="1">1</option>
                    </select>
                    <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.25rem' }}>
                      Merged by minimum with TxDefault. A 53 kW cap on a 20 kW fallback stays 20 kW.
                    </div>
                  </div>
                </div>
              </>
            )}

            {profileMode === 'cap' && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                <label style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                  Schedule Periods ({profileForm.periods.length}):
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const lastPeriod = profileForm.periods[profileForm.periods.length - 1];
                    const newStart = parseInt(lastPeriod.startPeriod) + 3600;
                    setProfileForm({
                      ...profileForm,
                      periods: [...profileForm.periods, {
                        startPeriod: newStart,
                        limit: lastPeriod.limit,
                        numberPhases: profileForm.number_phases
                      }]
                    });
                  }}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
                >
                  + Add Period
                </button>
              </div>
              <div style={{ fontSize: '0.75rem', color: '#666', marginBottom: '0.5rem' }}>
                Each period defines a power limit starting at a given offset (seconds from schedule start).
              </div>

              {profileForm.periods.map((period, index) => (
                <div key={index} style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr auto',
                  gap: '0.5rem',
                  alignItems: 'end',
                  marginBottom: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px',
                  border: '1px solid #e9ecef'
                }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                      Start (seconds):
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={period.startPeriod}
                      onChange={(e) => {
                        const updated = [...profileForm.periods];
                        updated[index] = { ...updated[index], startPeriod: e.target.value };
                        setProfileForm({ ...profileForm, periods: updated });
                      }}
                      style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                      Limit ({profileForm.charging_rate_unit}):
                    </label>
                    <input
                      type="number"
                      min="0"
                      max={profileForm.charging_rate_unit === 'W' ? '350000' : '500'}
                      step={profileForm.charging_rate_unit === 'W' ? '1000' : '1'}
                      value={period.limit}
                      onChange={(e) => {
                        const updated = [...profileForm.periods];
                        updated[index] = { ...updated[index], limit: e.target.value };
                        setProfileForm({ ...profileForm, periods: updated });
                      }}
                      style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: '#666', marginBottom: '0.25rem' }}>
                      Phases:
                    </label>
                    <select
                      value={period.numberPhases}
                      onChange={(e) => {
                        const updated = [...profileForm.periods];
                        updated[index] = { ...updated[index], numberPhases: parseInt(e.target.value) };
                        setProfileForm({ ...profileForm, periods: updated });
                      }}
                      style={{ width: '100%', padding: '0.4rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
                    >
                      <option value="1">1</option>
                      <option value="3">3</option>
                    </select>
                  </div>
                  <div>
                    {profileForm.periods.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updated = profileForm.periods.filter((_, i) => i !== index);
                          setProfileForm({ ...profileForm, periods: updated });
                        }}
                        style={{
                          padding: '0.4rem 0.6rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                        title="Remove this period"
                      >
                        X
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            )}

            <button
              type="submit"
              data-testid="set-profile-submit"
              disabled={profileLoading || charger.connection_status !== 'Connected'}
              className="btn btn-primary"
              style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', fontWeight: 'bold' }}
            >
              {profileLoading ? 'Setting Profile...' :
               profileMode === 'fallback' ? `Set Fallback (TxDefault stack 0, ${fallbackHoldLabel})` :
               profileMode === 'timed'
                 ? `Set Timed Limit (${formatDurationSecs(profileForm.duration)} then fallback, stack 0)`
                 : `Set ChargePointMax Cap (stack ${profileForm.stack_level})`}
            </button>
          </form>

          {/* Profile Preview */}
          <div
            data-testid="profile-preview"
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              background: profileMode === 'timed' ? '#fff8e1' : '#f0f7ff',
              borderRadius: '6px',
              fontSize: '0.9rem',
              border: profileMode === 'timed' ? '1px solid #ffe082' : '1px solid #cce0ff'
            }}
          >
            <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: profileMode === 'timed' ? '#f57c00' : '#0066cc' }}>
              Profile Preview
            </div>
            <div><strong>Connector:</strong> {profileMode === 'cap' || profileForm.connector_id === '0' || profileForm.connector_id === 0 ? 'All (0)' : `Connector ${profileForm.connector_id}`}</div>
            <div><strong>Kind:</strong> {profileMode === 'cap' ? `${profileForm.profile_kind}${profileForm.profile_kind === 'Recurring' ? ` (${profileForm.recurrency_kind})` : ''}` : 'Absolute'}</div>
            <div><strong>Rate Unit:</strong> {profileForm.charging_rate_unit}</div>
            <div><strong>Duration:</strong> {profileMode === 'fallback'
              ? fallbackHoldLabel
              : profileMode === 'timed'
                ? `${formatDurationSecs(profileForm.duration)} solar + ${fallbackHoldLabel} fallback`
                : formatDurationSecs(profileForm.duration)}</div>
            <div><strong>Priority:</strong> {profileMode === 'cap' ? `Level ${profileForm.stack_level}` : 'Level 0 (single TxDefault — this charger does not stack)'}</div>
            <div><strong>Purpose:</strong> {profileMode === 'cap' ? 'ChargePointMaxProfile' : 'TxDefaultProfile'}</div>
            <div style={{ marginTop: '0.5rem' }}><strong>Periods:</strong></div>
            {previewPeriods.map((p, i) => (
              <div key={i} style={{ marginLeft: '1rem', fontSize: '0.85rem' }}>
                Period {i + 1}: offset {p.startPeriod}s, {formatScheduleLimit(p.limit, profileForm.charging_rate_unit)}, {p.numberPhases} phase{p.numberPhases > 1 ? 's' : ''}
                {profileMode === 'timed' && i === 0 ? ' (timed / solar)' : ''}
                {profileMode === 'timed' && i === 1 ? ' (fallback)' : ''}
              </div>
            ))}
          </div>
        </div>

        {/* Clear Charging Profiles Section */}
        <div className="card" style={{ backgroundColor: '#fff5f5', border: '1px solid #fed7d7' }}>
          <h3 style={{ color: '#c53030', marginBottom: '1rem' }}>🗑️ Clear Charging Profiles</h3>
          <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1.5rem' }}>
            Remove existing charging profiles from the charger. Use with caution - cleared profiles cannot be recovered.
          </p>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            marginBottom: '1.5rem'
          }}>
            {/* Clear All Profiles */}
            <div style={{ 
              padding: '1rem', 
              border: '1px solid #fed7d7', 
              borderRadius: '6px',
              backgroundColor: '#fff'
            }}>
              <h4 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', color: '#c53030' }}>
                Clear All Profiles
              </h4>
              <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1rem' }}>
                Removes all charging profiles (defaults + temporary)
              </p>
              <button
                onClick={() => handleClearProfile('all')}
                disabled={clearLoadingMap['all'] || charger.connection_status !== 'Connected'}
                className="btn btn-danger"
                style={{ width: '100%', fontSize: '0.85rem' }}
              >
                {clearLoadingMap['all'] ? 'Clearing...' : 'Clear All'}
              </button>
            </div>

            {/* Clear Stack Level */}
            <div style={{ 
              padding: '1rem', 
              border: '1px solid #fed7d7', 
              borderRadius: '6px',
              backgroundColor: '#fff'
            }}>
              <h4 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', color: '#c53030' }}>
                Clear Stack Level {clearStackLevel}
              </h4>
              <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1rem' }}>
                Removes profiles at stack level {clearStackLevel} only
              </p>
              <button
                onClick={() => handleClearProfile('stack_level')}
                disabled={clearLoadingMap['stack_level'] || charger.connection_status !== 'Connected'}
                className="btn btn-warning"
                style={{ width: '100%', fontSize: '0.85rem' }}
              >
                {clearLoadingMap['stack_level'] ? 'Clearing...' : `Clear Level ${clearStackLevel}`}
              </button>
            </div>

            {/* Clear Default Profiles */}
            <div style={{ 
              padding: '1rem', 
              border: '1px solid #fed7d7', 
              borderRadius: '6px',
              backgroundColor: '#fff'
            }}>
              <h4 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', color: '#c53030' }}>
                Clear Default Profiles
              </h4>
              <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1rem' }}>
                Removes TxDefaultProfile (permanent fallbacks)
              </p>
              <button
                onClick={() => handleClearProfile('defaults')}
                disabled={clearLoadingMap['defaults'] || charger.connection_status !== 'Connected'}
                className="btn btn-warning"
                style={{ width: '100%', fontSize: '0.85rem' }}
              >
                {clearLoadingMap['defaults'] ? 'Clearing...' : 'Clear Defaults'}
              </button>
            </div>

            {/* Clear Temporary Profiles */}
            <div style={{ 
              padding: '1rem', 
              border: '1px solid #fed7d7', 
              borderRadius: '6px',
              backgroundColor: '#fff'
            }}>
              <h4 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem 0', color: '#c53030' }}>
                Clear Temporary Profiles
              </h4>
              <p style={{ fontSize: '0.8rem', color: '#666', marginBottom: '1rem' }}>
                Removes ChargePointMaxProfile (keeps defaults)
              </p>
              <button
                onClick={() => handleClearProfile('temporary')}
                disabled={clearLoadingMap['temporary'] || charger.connection_status !== 'Connected'}
                className="btn btn-warning"
                style={{ width: '100%', fontSize: '0.85rem' }}
              >
                {clearLoadingMap['temporary'] ? 'Clearing...' : 'Clear Temporary'}
              </button>
            </div>
          </div>

          {/* Warning Notice */}
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#fed7d7', 
            borderRadius: '6px',
            border: '1px solid #fc8181'
          }}>
            <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#c53030' }}>
              ⚠️ Important Notes
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', fontSize: '0.85rem', color: '#744210' }}>
              <li><strong>Clear All:</strong> Removes all profiles - charger will use built-in defaults</li>
              <li><strong>Stack Level:</strong> Only removes profiles at the selected priority level</li>
              <li><strong>Defaults:</strong> Removes permanent fallback profiles (TxDefaultProfile)</li>
              <li><strong>Temporary:</strong> Removes time-limited profiles, keeps permanent defaults</li>
              <li><strong>Recovery:</strong> Cleared profiles cannot be restored - you'll need to recreate them</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargerDetails;